import type { VideoSettings, SubtitleSettings, SubtitleStyle } from "@/types";

export const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
	fps: 30,
	enableTransition: false,
	transitionSound: "none",
	transitionColor: "#000000",
	defaultAdditionalTime: 0.5,
	enableAudioNormalization: true,
	targetLoudness: -16,
};

export const DEFAULT_SPEED = 1.0;

export const STORAGE_PREFIX = "videocreator-";
export const STORAGE_KEYS = {
	SETTINGS: `${STORAGE_PREFIX}settings`,
} as const;

export const INDEXED_DB_NAME = "videocreator-db";
export const INDEXED_DB_VERSION = 1;
export const INDEXED_DB_STORES = {
	IMAGES: "images",
	AUDIOS: "audios",
} as const;

export const MAX_SCENE_TEXT_LENGTH = 1000;

export const IMAGE_VALIDATION = {
	MAX_SIZE: 15 * 1024 * 1024,
	MAX_COUNT: 40,
	MAX_TOTAL_SIZE: 350 * 1024 * 1024,
} as const;

export const AUDIO_VALIDATION = {
	MAX_SIZE: 7 * 1024 * 1024,
} as const;

export const DEFAULT_ELEVENLABS_MODEL = "eleven_v3";
export const DEFAULT_VOICE_STABILITY = 0.5;
export const DEFAULT_VOICE_SIMILARITY_BOOST = 0.75;

export const ELEVENLABS_API = {
	MODEL: DEFAULT_ELEVENLABS_MODEL,
	STABILITY: DEFAULT_VOICE_STABILITY,
	SIMILARITY_BOOST: DEFAULT_VOICE_SIMILARITY_BOOST,
	TIMEOUT_SECONDS: 30,
	MAX_RETRIES: 3,
} as const;

export const TRANSITION_SOUND_CONFIG = {
	none: { name: "なし", file: null },
	page_turn: { name: "ページめくり", file: "/assets/sounds/page_turn.mp3" },
} as const;

export const TRANSITION_SOUNDS = Object.entries(TRANSITION_SOUND_CONFIG).map(
	([id, config]) => ({ id, ...config })
);

export function getTransitionSoundFile(id: string): string | null {
	return (
		TRANSITION_SOUND_CONFIG[id as keyof typeof TRANSITION_SOUND_CONFIG]
			?.file ?? null
	);
}

export const FPS_OPTIONS = [24, 30, 60] as const;

export const LOUDNESS_PRESETS = [
	{ value: -23, label: "EBU R128" },
	{ value: -16, label: "YouTube/Spotify" },
	{ value: -14, label: "Apple Music" },
] as const;

export const AUDIO_GENERATION = {
	CONCURRENT_LIMIT: 2,
} as const;

export const VOICE_SEARCH = {
	PAGE_SIZE: 10,
} as const;

// シーンの追加表示時間（前後の間隔）の入力範囲（秒）
// デフォルト値設定と各シーンの個別設定で共通の範囲を使う
export const ADDITIONAL_TIME = {
	MIN: 0,
	MAX: 10,
	STEP: 0.1,
} as const;

// テロップ用フォント設定
export const SUBTITLE_FONTS = [
	{
		id: "Noto Sans JP",
		name: "Noto Sans JP",
		displayName: "ゴシック体",
		url: "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700&display=swap",
		// ffmpeg用のフォントファイルURL（woff2）
		fontFileUrl: "https://fonts.gstatic.com/s/notosansjp/v52/-F6jfjtqLzI2JPCgQBnw7HFyzSD-AsregP8VFJEk757Y0rw_qMHVdbR2L8Y9QTJ1LwkRmR5GprQAe-T30bU.0.woff2",
	},
	{
		id: "Noto Serif JP",
		name: "Noto Serif JP",
		displayName: "明朝体",
		url: "https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;700&display=swap",
		fontFileUrl: "https://fonts.gstatic.com/s/notoserifjp/v21/xn7mYHs72GKoTvER4Gn3b5eMXNikYkY0T84.woff2",
	},
	{
		id: "M PLUS Rounded 1c",
		name: "M PLUS Rounded 1c",
		displayName: "丸ゴシック",
		url: "https://fonts.googleapis.com/css2?family=M+PLUS+Rounded+1c:wght@400;700&display=swap",
		fontFileUrl: "https://fonts.gstatic.com/s/mplusrounded1c/v15/VdGCAYIAV6gnpUpoWwNkYvrugw9RuM3ixLsg6-av1x0.woff2",
	},
	{
		id: "Zen Kaku Gothic New",
		name: "Zen Kaku Gothic New",
		displayName: "モダンゴシック",
		url: "https://fonts.googleapis.com/css2?family=Zen+Kaku+Gothic+New:wght@400;700&display=swap",
		fontFileUrl: "https://fonts.gstatic.com/s/zenkakugothicnew/v15/gNMYW2drQpDw0GjzrVNFf_valaDBcznOqpdKaWTSTGlMyd8.woff2",
	},
] as const;

// テロップデフォルトスタイル
export const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = {
	fontFamily: "Noto Sans JP",
	fontSize: 48,
	fontWeight: "normal",
	textColor: "#FFFFFF",
	strokeColor: "#000000",
	strokeWidth: 2,
	backgroundColor: "#000000",
	backgroundOpacity: 50,
};

// テロップデフォルト設定
export const DEFAULT_SUBTITLE_SETTINGS: SubtitleSettings = {
	positionFromBottom: 80,
	horizontalPadding: 40,
	lineHeight: 1.4,
	letterSpacing: 0,
	defaultStyle: DEFAULT_SUBTITLE_STYLE,
};

// フォントファミリーのバリデーション関数
export function isValidFontFamily(value: string): boolean {
	return SUBTITLE_FONTS.some(f => f.id === value);
}

// フォントIDからフォント設定を取得
export function getFontConfig(fontId: string) {
	return SUBTITLE_FONTS.find(f => f.id === fontId);
}
